import type { FormFieldOption } from '@risksmart-app/form-configuration/src/types';
import type { CustomAttributeSchema } from 'src/components/form/custom-attributes/CustomAttributeSchema';
import { fieldTypesConfig } from 'src/components/form/custom-attributes/field-types';

import type { JSONObject } from '@/types/types';

import { getCustomAttributeRenderProps } from './customAttributes';

/**
 * One logical custom field that exists on more than one of the merged schemas.
 * `primaryPath` keeps its column; `aliasPaths` are the sibling paths it absorbs.
 */
export type CustomAttributeAlias = {
  primaryPath: string;
  aliasPaths: string[];
};

/**
 * Shared empty default for the hooks' optional `aliases` — a fresh `[]` per
 * render would invalidate their `useMemo`s for every caller that omits it.
 */
export const NO_ALIASES: CustomAttributeAlias[] = [];

type Candidate = {
  schemaIndex: number;
  path: string;
  label: string;
  type: string;
  altLabel: string | undefined;
  options: FormFieldOption[] | undefined;
};

/**
 * Two members may only merge when they would render the SAME columns.
 *
 * `GeneratedId` (a per-schema UUID) is excluded and options are sorted by value,
 * since option order is a form-editor detail rather than part of a field's
 * identity. `altLabel` is included because it alone decides whether an `_alt`
 * column is emitted, so merging across a mismatch would silently drop a column
 * whenever the member lacking it won the primary election.
 */
const presentationFingerprint = (candidate: Candidate): string =>
  JSON.stringify({
    altLabel: candidate.altLabel?.trim() ?? '',
    options: (candidate.options ?? [])
      .map((option) => ({
        _tag: option._tag,
        Value: option.Value,
        AltValue: 'AltValue' in option ? option.AltValue : undefined,
      }))
      .sort((a, b) => a.Value.localeCompare(b.Value)),
  });

/**
 * True when the row holds a usable value at `path`, so a sibling that would
 * render as blank never wins the alias lookup over one holding a real value.
 *
 * Non-string, non-array scalars count as present, which is what number fields
 * need — `0` is a real value, and they read through `getNumericValue` rather
 * than `matchToField` (which renders every non-string scalar as empty).
 */
const hasValueAt = (data: JSONObject, path: string): boolean => {
  const value = data[path];

  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.length > 0;
  }

  return true;
};

/**
 * Identifies custom fields that the same register sources from more than one
 * schema.
 *
 * Rating registers list rows of both control types in one table, and each
 * control type has its own `form_configuration` row. Custom-field ids are minted
 * per schema (`${timestamp}_${fieldType}`), so adding one logical field — say
 * "Completed By" — to both rating forms produces two unrelated paths, and each
 * column can then only ever populate the rows of its own control type (CUS-209).
 *
 * Grouping is deliberately conservative: identical trimmed label and field type,
 * spanning at least two schemas, exactly one member per schema, and equivalent
 * option sets. Anything ambiguous keeps its existing separate columns, and fewer
 * than two schemas returns `[]` so single-schema registers are inert.
 */
export const buildCustomAttributeAliases = ({
  customAttributeSchemas,
  hiddenPaths,
}: {
  customAttributeSchemas: CustomAttributeSchema[];
  hiddenPaths?: Set<string>;
}): CustomAttributeAlias[] => {
  if (customAttributeSchemas.length < 2) {
    return [];
  }

  const candidates: Candidate[] = [];

  customAttributeSchemas.forEach((schema, schemaIndex) => {
    if (!schema?.Schema || !schema?.UiSchema) {
      return;
    }

    for (const renderProps of getCustomAttributeRenderProps(
      schema.Schema,
      schema.UiSchema
    )) {
      // Mirrors `convertSchemaToFieldConfig`'s guards: a field that produces no
      // column must produce no alias either. For a hidden path that matters in
      // both directions — aliasing it would drop the sibling's still-visible
      // column and leave the field with none at all (CUS-216).
      const fieldTypeConfig = fieldTypesConfig[renderProps.type];
      if (
        !fieldTypeConfig ||
        fieldTypeConfig.excludeFromReporting ||
        hiddenPaths?.has(renderProps.path)
      ) {
        continue;
      }

      candidates.push({
        schemaIndex,
        path: renderProps.path,
        label: renderProps.label?.trim() ?? '',
        type: renderProps.type,
        altLabel: renderProps.altLabel,
        options: renderProps.options,
      });
    }
  });

  const groups = new Map<string, Candidate[]>();
  for (const candidate of candidates) {
    if (!candidate.label) {
      continue;
    }
    const key = `${candidate.type}::${candidate.label}`;
    groups.set(key, [...(groups.get(key) ?? []), candidate]);
  }

  const aliases: CustomAttributeAlias[] = [];

  for (const members of groups.values()) {
    const schemaIndexes = new Set(members.map((m) => m.schemaIndex));

    // Must span multiple schemas, with an unambiguous one-per-schema pairing.
    if (schemaIndexes.size < 2 || schemaIndexes.size !== members.length) {
      continue;
    }

    const fingerprints = new Set(members.map(presentationFingerprint));
    if (fingerprints.size > 1) {
      continue;
    }

    // Lexicographic, NOT positional: the schemas reach us in query-result order
    // rather than the order the register declares, so a positional primary would
    // flip between loads and churn saved column preferences.
    const paths = members.map((m) => m.path).sort((a, b) => a.localeCompare(b));

    aliases.push({
      primaryPath: paths[0],
      aliasPaths: paths.slice(1),
    });
  }

  return aliases;
};

export const getAliasedPaths = (aliases: CustomAttributeAlias[]): Set<string> =>
  new Set(aliases.flatMap((alias) => alias.aliasPaths));

/**
 * Projects a row's custom-attribute values onto their primary paths, so the
 * surviving column reads the value this row actually recorded.
 *
 * Values are only ever moved *within a row*, never copied between rows, so a
 * rating that never stored the field stays empty. Returns the input reference
 * when there is nothing to project, keeping the row identity and `useMemo` deps
 * downstream stable.
 */
export const applyCustomAttributeAliases = (
  data: JSONObject | null | undefined,
  aliases: CustomAttributeAlias[]
): JSONObject | null | undefined => {
  if (!data || aliases.length === 0) {
    return data;
  }

  let projected: JSONObject | undefined;

  for (const { primaryPath, aliasPaths } of aliases) {
    if (hasValueAt(data, primaryPath)) {
      continue;
    }

    const source = aliasPaths.find((path) => hasValueAt(data, path));
    if (!source) {
      continue;
    }

    projected ??= { ...data };
    projected[primaryPath] = data[source];
  }

  return projected ?? data;
};
